import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import { z } from 'zod';
import { useConfig, useOrdenesAbiertas } from '@/datos/consultas';
import { permisos, useSesionActiva } from '@/lib/sesion';
import { useAhora } from '@/lib/tiempo';
import { Encabezado } from '@/shell/Encabezado';
import { estaAtrasada, vistaTablero } from '@/tablero/logica';
import { useOrdenesVisibles } from '@/tablero/movimientos';
import { TableroEscritorio, TableroSinAtrasados, TableroVacio } from '@/tablero/TableroEscritorio';
import { TableroMovil } from '@/tablero/TableroMovil';
import { useAccionesOrden } from '@/tablero/useAccionesOrden';
import { ErrorCarga, Esqueleto } from '@/ui/basicos';
import { cn } from '@/ui/cn';
import { useEsEscritorio } from '@/lib/medios';

const busqueda = z.object({
  atrasados: z.boolean().optional().catch(undefined),
  fase: z.number().int().min(0).optional().catch(undefined),
});

export const Route = createFileRoute('/_app/tablero')({
  validateSearch: busqueda,
  component: Tablero,
});

function Tablero() {
  const { atrasados, fase } = Route.useSearch();
  const navegar = useNavigate({ from: Route.fullPath });
  const s = useSesionActiva();
  const config = useConfig();
  const servidor = useOrdenesAbiertas();
  const ordenes = useOrdenesVisibles(servidor.data);
  const ahora = useAhora();
  const fases = config.data?.fases ?? [];
  const acciones = useAccionesOrden(ordenes, fases);
  const escritorio = useEsEscritorio();
  const puedeMover = permisos.moverAutos(s.rol);

  const enTaller = ordenes?.length ?? 0;
  const nAtrasados = ordenes?.filter((o) => estaAtrasada(o, fases, ahora)).length ?? 0;
  const subtitulo = ordenes
    ? `${enTaller} ${enTaller === 1 ? 'vehículo' : 'vehículos'} en taller · ${nAtrasados} ${nAtrasados === 1 ? 'atrasado' : 'atrasados'}`
    : 'Cargando…';

  const verTodos = () =>
    void navegar({ search: (p) => ({ ...p, atrasados: undefined }), replace: true });
  const vista = vistaTablero({
    error: servidor.isError,
    cargando: !ordenes || !config.data,
    total: enTaller,
    soloAtrasados: !!atrasados,
    atrasados: nAtrasados,
  });

  const filtro = (
    <button
      type="button"
      aria-pressed={!!atrasados}
      onClick={() =>
        void navegar({
          search: (p) => ({ ...p, atrasados: p.atrasados ? undefined : true }),
          replace: true,
        })
      }
      className={cn(
        'flex h-11 items-center gap-2 rounded-[10px] border px-3.5 text-[15px] transition-[background-color,border-color,color] duration-150 lg:h-[42px] lg:px-4',
        atrasados
          ? 'border-atraso-borde bg-ray-soft font-bold text-atraso-texto'
          : 'border-border-input bg-surface text-text hover:bg-surface-muted',
      )}
    >
      <AlertTriangle className={cn('size-4', atrasados ? 'text-atraso-texto' : 'text-text-2')} />
      <span className="hidden sm:inline">Solo atrasados</span>
      {nAtrasados > 0 && <span className="tabular text-[13px] sm:hidden">{nAtrasados}</span>}
    </button>
  );

  return (
    <>
      <Encabezado titulo="Tablero" ayuda="tablero" subtitulo={subtitulo} acciones={filtro} />
      {vista === 'error' ? (
        <div className="p-6">
          <ErrorCarga error={servidor.error} />
        </div>
      ) : vista === 'cargando' || !ordenes ? (
        <EsqueletoTablero />
      ) : vista === 'vacio' ? (
        <TableroVacio />
      ) : vista === 'sin_atrasados' ? (
        <TableroSinAtrasados onVerTodos={verTodos} />
      ) : escritorio ? (
        <div className="flex h-[calc(100dvh-76px)] min-h-[560px] flex-col">
          <TableroEscritorio
            ordenes={ordenes}
            fases={fases}
            ahora={ahora}
            soloAtrasados={!!atrasados}
            acciones={acciones}
            puedeMover={puedeMover}
          />
        </div>
      ) : (
        <TableroMovil
          ordenes={ordenes}
          fases={fases}
          ocupacion={acciones.ocupacion}
          ahora={ahora}
          fase={Math.min(fase ?? primeraConAutos(acciones.ocupacion), fases.length - 1)}
          onFase={(i) => void navegar({ search: (p) => ({ ...p, fase: i }), replace: true })}
          soloAtrasados={!!atrasados}
        />
      )}
      {acciones.dialogos}
    </>
  );
}

function primeraConAutos(ocupacion: number[]) {
  const i = ocupacion.findIndex((n) => n > 0);
  return i === -1 ? 0 : i;
}

function EsqueletoTablero() {
  return (
    <div
      className="grid flex-1 auto-cols-[minmax(112px,1fr)] grid-flow-col gap-2.5 overflow-hidden px-5 py-[18px]"
      aria-busy="true"
      aria-label="Cargando tablero"
    >
      {Array.from({ length: 9 }, (_, i) => (
        <div key={i} className="flex flex-col gap-2 rounded-[14px] bg-surface-muted p-2.5">
          <Esqueleto className="h-4 w-3/4 bg-cupo-pista" />
          <Esqueleto className="mt-3 h-1.5 w-full bg-cupo-pista" />
          {Array.from({ length: ((i * 7) % 4) + 1 }, (_, j) => (
            <Esqueleto key={j} className="h-[72px] rounded-[10px] bg-surface" />
          ))}
        </div>
      ))}
    </div>
  );
}
